/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useState, useEffect } from 'react'
import { useServiceContext } from '../../../../root'
import ModalConfirmation from '../../../../components/shared/ModalConfirmation'
import HtmlCell from '../../../../components/shared/HtmlCell'
import { toast } from '../../../../services/toast'
import { documentTypesApiService } from '../../../../services/api/api.documenttypes'

interface ModalRemoveProps {
  documentType: any
  onClose: () => void
  onSuccess?: () => void
}

const ModalRemove: React.FC<ModalRemoveProps> = ({ documentType, onClose, onSuccess }) => {
  const context = useServiceContext()
  const { translate } = context.service.filter

  const [msg, setMsg] = useState<React.ReactNode>('')
  const [loading, setLoading] = useState<Promise<any> | null>(null)

  useEffect(() => {
    selectMessage()
  }, [])

  const selectMessage = async () => {
    try {
      const promise = documentTypesApiService.documentUsage(documentType.id)
      setLoading(promise)

      const data: any = await promise
      const usedDocTypes = data.value
      const doctypes: string[] = data.value !== null ? [...usedDocTypes] : []

      const textMsg = (
        <div>
          {!!usedDocTypes.length && (
            <>
              <HtmlCell>
                {translate('DOCUMENT_TYPES_DELETE_MSG_CANNOT_BE_DELETED', { name: documentType.name })}
              </HtmlCell>
              <HtmlCell>{`${doctypes.join('<br/> ')}`}</HtmlCell>
            </>
          )}
          {!usedDocTypes.length && (
            <HtmlCell>{translate('SETTINGS_DOC_TYPES_REMOVE_Q') + ' <b>' + documentType.name + '</b>'}</HtmlCell>
          )}
        </div>
      )

      setMsg(textMsg)
      setLoading(null)
    } catch (error) {
      setLoading(null)
    }
  }

  const doSave = async () => {
    try {
      await documentTypesApiService.remove(documentType.id)
      toast.success(translate('DOCUMENT_TYPES_DELETE_SUCCESS_MSG'))

      if (onSuccess) {
        onSuccess()
      }
      onClose()
    } catch (error: any) {
      const errorMsg = error?.data?.message || error?.message || 'Unknown error'
      toast.error(translate('DOCUMENT_TYPES_DELETE_FAIL_MSG') + ': ' + errorMsg)
    }
  }

  return (
    <ModalConfirmation
      titleMsg={translate('DOCUMENT_TYPES_DELETE_TITLE')}
      loading={loading}
      msg={msg}
      cancelTxt={translate('CANCEL')}
      okTxt={translate('OK')}
      onSubmit={doSave}
      onCancel={onClose}
    />
  )
}

export default ModalRemove
